import {
  type PublicShare,
  type PublicShareAsset,
  type PublicShareAssetPage,
  publicShareAssetPageSchema,
  publicShareSchema,
} from '@xphoto/shared';

/**
 * Client de la surface PUBLIQUE des partages (XPHOTO-31, ADR-0009).
 *
 * ── POURQUOI CE MODULE N'UTILISE PAS `apiFetch` ───────────────────────────────────
 *
 * C'est le point le plus important du fichier, et il n'est pas cosmétique.
 * `lib/http.ts` fait DEUX choses incompatibles avec une page publique :
 *
 *  1. il pose l'en-tête `X-Active-Slot` du compte courant — un visiteur DÉJÀ CONNECTÉ
 *     ouvrant un lien partagé enverrait donc son identité sur une route qui n'en veut
 *     pas, et le partage cesserait d'être « anonyme » au sens strict ;
 *  2. il notifie tout 401 hors `/api/auth/*` à `AuthProvider`, qui renvoie vers
 *     l'écran de connexion — exactement ce qu'un visiteur sans compte ne doit JAMAIS
 *     voir en cliquant sur un lien qu'on lui a envoyé.
 *
 * D'où `fetch` nu, sans `credentials` (rien à envoyer, rien à recevoir : la surface
 * publique ne pose aucun cookie, et un test le vérifie côté API).
 *
 * ── LES URL DE MÉDIA SONT DES ATTRIBUTS `src` ─────────────────────────────────────
 *
 * `thumbnailUrl`/`mediaUrl` ne sont pas des requêtes : elles alimentent des `<img>` et
 * des `<video>`, que le navigateur charge lui-même. Elles ne passent donc SURTOUT pas
 * par `withSlotParam` (le repli query du multi-login) — c'est précisément là que vit le
 * couplage à la session dans `gallery/api.ts`.
 */

/** Le jeton vient de l'URL : il doit être encodé, jamais concaténé tel quel. */
function base(token: string): string {
  return `/api/public/shares/${encodeURIComponent(token)}`;
}

/**
 * Distingue « ce partage n'existe pas / plus » du reste. L'API rend le MÊME 404 pour
 * un jeton inconnu, révoqué, expiré ou dont le créateur est parti : le client ne peut
 * donc pas être plus précis que la page ne doit l'être.
 */
export class ShareNotFoundError extends Error {
  constructor() {
    super('share-not-found');
    this.name = 'ShareNotFoundError';
  }
}

async function getJson(url: string, signal?: AbortSignal): Promise<unknown> {
  // `signal` n'est posé que s'il existe : sous `exactOptionalPropertyTypes`, un
  // `signal: undefined` explicite n'est pas la même chose qu'une clé absente.
  const init: RequestInit = signal === undefined ? {} : { signal };
  const res = await fetch(url, init);
  if (res.status === 404) throw new ShareNotFoundError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** En-tête du partage : titre, nombre de médias, et si l'original est téléchargeable. */
export async function fetchShare(token: string, signal?: AbortSignal): Promise<PublicShare> {
  return publicShareSchema.parse(await getJson(base(token), signal));
}

/** Une page de médias. `cursor` est OPAQUE : il vient tel quel de la réponse précédente. */
export async function fetchShareAssets(
  token: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<PublicShareAssetPage> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor !== null) params.set('cursor', cursor);
  return publicShareAssetPageSchema.parse(
    await getJson(`${base(token)}/assets?${params.toString()}`, signal),
  );
}

/** Taille de page — le maximum accepté par le contrat : un album se charge d'un trait. */
export const PAGE_SIZE = 200;

/** URL de vignette (attribut `src`). Immuable côté serveur, donc cachable à vie. */
export function thumbnailUrl(token: string, asset: PublicShareAsset, size: 256 | 1024): string {
  return `${base(token)}/assets/${asset.id}/thumbnail?size=${size}`;
}

/** URL du média servi (l'original, ou la copie allégée si le partage le prévoit). */
export function mediaUrl(token: string, asset: PublicShareAsset): string {
  return `${base(token)}/assets/${asset.id}/media`;
}
